/**
 * @fileoverview
 * Lead Magnet Section (Free Resource/Sketch Optimization Guide) for the homepage.
 *
 * This component renders:
 * - Centered "Free Resource" eyebrow and "Sketch Optimization Guide" heading
 * - Learning points separated by divider lines
 * - Horizontal inline form (name + email) with full-width submit button
 * - Turnstile widget for bot protection
 * - Form validation and submission handling
 *
 * Fulfills: VAL-HOME-019
 */

"use client";

import { useState, useRef, useCallback } from "react";

import { Trans, useLingui } from "@lingui/react/macro";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@/components/TurnstileWidget";

import { User, Mail, Check } from "lucide-react";
import { SectionShell, SectionContainer, SectionEyebrow } from "../primitives";

type FormState = {
  name: string;
  email: string;
  isSubmitting: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string;
};

const learningPoints = [
  "How to annotate sketches for AI",
  "50+ Styles Cheat Sheet",
  'Stop losing clients to "I can\'t visualize it"',
];

/**
 * LeadMagnetSection renders the Sketch Optimization Guide section.
 *
 * @returns JSX.Element
 */
export function LeadMagnetSection() {
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const { i18n, t } = useLingui();

  const [formState, setFormState] = useState<FormState>({
    name: "",
    email: "",
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    errorMessage: "",
  });

  const handleTurnstileToken = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    // Don't show an error pre-emptively. Turnstile can fail on local dev due to
    // domain-mismatch with a real site key, or on slow connections before the
    // script loads. The submit handler already rejects when no token is present
    // and shows "Please complete the verification." to the user at that point.
    // Showing an error here creates a visible height gap on localhost that does
    // not exist on production (M5-LEAD-001 gap root cause).
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formState.name || !formState.email) {
      setFormState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: "Please fill in all required fields.",
      }));
      return;
    }

    // Turnstile validation - only if site key is configured
    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken) {
      setFormState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: "Please complete the verification.",
      }));
      return;
    }

    setFormState((prev) => ({ ...prev, isSubmitting: true, isError: false }));

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          subject: "Lead Magnet - Sketch Optimization Guide",
          source: "lead-magnet",
          message: "Lead magnet request for Sketch Optimization Guide.",
          turnstileToken,
        }),
      });

      if (response.ok) {
        setFormState((prev) => ({
          ...prev,
          isSubmitting: false,
          isSuccess: true,
          name: "",
          email: "",
        }));
        // Reset Turnstile
        turnstileRef.current?.reset();
        setTurnstileToken("");
      } else {
        throw new Error("Form submission failed");
      }
    } catch {
      setFormState((prev) => ({
        ...prev,
        isSubmitting: false,
        isError: true,
        errorMessage: "Oops! Something went wrong while submitting the form.",
      }));
    }
  };
  const sectionAriaLabel = i18n.locale === "vi" ? "Phần tài nguyên miễn phí" : "Lead magnet section";

  return (
    <SectionShell
      id="contact"
      ariaLabel={sectionAriaLabel}
      data-testid="lead-magnet-section"
      variant="default"
      noPadding
      className="py-6 sm:py-14 lg:py-20"
    >
      <SectionContainer>
        {/* Full-width centered heading above the 2-column layout */}
        <div className="mb-8 text-center">
          <SectionEyebrow align="center" backgroundClassName="bg-transparent" label={<Trans>Free Resource</Trans>} />
          <h2 className="mt-2 text-2xl font-bold leading-tight text-gray-900 sm:text-3xl md:text-4xl lg:text-[2.5rem]">
            <Trans>Sketch Optimization Guide</Trans>
          </h2>
        </div>

        {/* 2-column layout: content left, form right */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">

          {/* Left Column: Learning Points */}
          <div>
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                <Trans>What you&apos;ll learn</Trans>
              </h3>
              <div className="divide-y divide-gray-200">
                {learningPoints.map((point, index) => (
                  <p key={index} className="py-3 text-gray-700">
                    <Trans>{point}</Trans>
                  </p>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div>
            {!formState.isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name + Email side-by-side */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Name Input */}
                  <div className="relative">
                    <User className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      placeholder={t`Your name *`}
                      required
                      aria-required="true"
                      value={formState.name}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, name: e.target.value }))
                      }
                      disabled={formState.isSubmitting}
                      className="h-12 border-gray-200 pr-12"
                    />
                  </div>

                  {/* Email Input */}
                  <div className="relative">
                    <Mail className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="email"
                      name="email"
                      id="email"
                      placeholder={t`Work email *`}
                      required
                      aria-required="true"
                      value={formState.email}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, email: e.target.value }))
                      }
                      disabled={formState.isSubmitting}
                      className="h-12 border-gray-200 pr-12"
                    />
                  </div>
                </div>

                {/* Submit Button — pure black */}
                <Button
                  type="submit"
                  disabled={formState.isSubmitting}
                  className="h-12 w-full bg-black text-white hover:bg-gray-900 disabled:opacity-50"
                >
                  {formState.isSubmitting ? <Trans>Please wait...</Trans> : <Trans>Send Me The Guide</Trans>}
                </Button>

                {/* Turnstile Widget */}
                <div className="pt-1">
                  <TurnstileWidget
                    ref={turnstileRef}
                    onToken={handleTurnstileToken}
                    onError={handleTurnstileError}
                    action="lead_magnet"
                    theme="auto"
                    size="normal"
                    className="flex justify-center"
                  />
                </div>

                {/* Error Message */}
                {formState.isError && (
                  <div
                    role="alert"
                    className="rounded-md bg-red-50 p-3 text-sm text-red-600"
                  >
                    {formState.errorMessage}
                  </div>
                )}
              </form>
            ) : (
              /* Success Message */
              <div
                role="alert"
                className="rounded-lg border border-green-200 bg-green-50 p-6 text-center"
              >
                <div className="mb-4 flex justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-green-800">
                  <Trans>Thank you!</Trans>
                </h3>
                <p className="text-green-700">
                  <Trans>The guide is on its way to your inbox.</Trans>
                </p>
              </div>
            )}
          </div>

        </div>
      </SectionContainer>
    </SectionShell>
  );
}
